import React, { useState, useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Panel from 'components/common/panel/panel';
import FlexibleRow from 'components/common/flexibleRow/flexibleRow';
import { useLocation, useHistory } from 'hooks/router';
import Heading from 'components/common/heading/heading';
import SoftwareUpdateHelper from 'components/navigation/softwareUpdate/softwareUpdateHelper';
import SoftwareUpdateFilter from 'components/navigation/softwareUpdate/softwareUpdateFilter';
import SoftwareUpdateService from 'services/softwareUpdateService';
import transformSoftwareUpdateFilters from './transformSoftwareUpdateFilters';
import AppContext from 'contexts/appContext';
import moment from 'moment';

const defaultPageSize = 10;

export default function SoftwareUpdatePage() {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const [filters, setFilters] = useState();
  const [pageNextOffset, setPageNextOffset] = useState(0);
  const [updateList, setUpdateList] = useState();
  const location = useLocation();
  const history = useHistory();
  const [showMoreVisible, setShowMoreVisible] = useState(true);
  const [filterPopupVisible, setFilterPopupVisible] = useState(false);

  useAsyncEffect(async () => {
    setAppArea(AppContext.Areas.SoftwareUpdate, 'Update History');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Software Update', selected: true }]);

    const useFilter = transformSoftwareUpdateFilters(filters);
    if (!useFilter.pageSize) {
      useFilter.pageSize = defaultPageSize;
    }
    setPageNextOffset(useFilter.pageSize);
    const updates = await SoftwareUpdateService.API.getSoftwareUpdates(useFilter);

    if (useFilter.pageSize >= defaultPageSize) {
      setShowMoreVisible(!(updates.length < defaultPageSize));
    }

    setUpdateList(updates);
  }, [location, filters]);

  const onShowMore = async () => {
    const updates = await SoftwareUpdateService.API.getSoftwareUpdates({ ...filters, pageSize: defaultPageSize, pageOffset: pageNextOffset });
    setShowMoreVisible(!(updates.length < defaultPageSize));
    setPageNextOffset(pageNextOffset + defaultPageSize);
    setUpdateList(updateList.concat(updates));
  };

  const renderRows = () => {
    if (updateList && updateList.length === 0) {
      return <div>No updates found.</div>;
    }
    return updateList && updateList.map((item, index) => SoftwareUpdateRow(item, index, history));
  };

  if (!updateList) {
    //Loading View
    return (
      <>
        <Heading />

        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-12 col-md-10 col-xl-8'>
            <Panel>
              <Panel.SkeletonHeader width={200} />
              <Panel.SkeletonBody rows={10} rowHeight={34} rowMargin={24} />
            </Panel>
          </div>
        </div>
      </>
    );
  }

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <div className='flex-grow-1 d-flex justify-content-end'>
            <Inputs.Button variant='primary' href='/SoftwareUpdate/Create' iconClass='icons8-plus-math' label='Create Update' />
          </div>
        </Heading>
      </Flow.Header>

      <Flow.Body>
        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-12 col-md-10 col-xl-8'>
            <Panel>
              <Panel.Header tools={<Inputs.Button variant='hover-default' iconClass='icons8-filter' onClick={() => setFilterPopupVisible(true)} />}>
                Updates
              </Panel.Header>
              <Panel.Body>
                <ul className='list-group list-group-flush'>
                  {renderRows()}
                </ul>
                {!showMoreVisible ? null : (
                  <div className='mt-3'>
                    <Inputs.Button variant='hover-default' block onClick={onShowMore} label='Show More' iconClass='icons8-down-arrow' />
                  </div>
                )}
              </Panel.Body>
            </Panel>
          </div>
        </div>

        <SoftwareUpdateFilter
          filters={filters}
          show={filterPopupVisible}
          onClose={() => setFilterPopupVisible(false)}
          onChange={(filters) => {
            setFilters(filters);
            setFilterPopupVisible(false);
          }}
        />
      </Flow.Body>
    </Flow>
  );
}

function SoftwareUpdateRow(item, index, history) {
  const renderPendingChildCount = () => {
    const count = item.activeChildCount - item.successfulChildCount - item.failedChildCount - item.progressChildCount;
    if (count === 0) return null;
    return (
      <FlexibleRow.Column>
        <span className='badge badge-light-primary badge-md'>Pending ({count})</span>
      </FlexibleRow.Column>
    );
  };

  const renderProgressChildCount = () => {
    const count = item.progressChildCount;
    if (count === 0) return null;
    return (
      <FlexibleRow.Column>
        <span className='badge badge-light-secondary badge-md'>In Progress ({count})</span>
      </FlexibleRow.Column>
    );
  };

  const renderSuccessfulChildCount = () => {
    const count = item.successfulChildCount;
    if (count === 0) return null;
    return (
      <FlexibleRow.Column>
        <span className='badge badge-light-success badge-md'>Successful ({count})</span>
      </FlexibleRow.Column>
    );
  };

  const renderFailureChildCount = () => {
    const count = item.failedChildCount;
    if (count === 0) return null;
    return (
      <FlexibleRow.Column>
        <span className='badge badge-light-danger badge-md'>Failed ({count})</span>
      </FlexibleRow.Column>
    );
  };

  const renderUpdateDateTime = () => {
    if (item.distributeOnHold)
      return 'On Hold';
    else
      return moment(item.distributeDateTime).format('ddd M/D/YY') + ' ' + (item.distributeAsap ? 'ASAP' : moment(item.distributeDateTime).format('h:mma'));
  }

  return (
    <a href={`/SoftwareUpdate/${item.softwareUpdateId}`} className='list-group-item list-group-item-action' key={index}>
      <FlexibleRow>
        <FlexibleRow.Stackable width='10%' minWidth='44px'>
          <FlexibleRow.Column className='px-0'>
            <div className='d-flex justify-content-center align-items-center rounded text-white' style={{ height: 32, width: 32, fontSize: 24, lineHeight: 1, backgroundColor: SoftwareUpdateHelper.getUpdateStatusColor(item.statusId) }}>
              {SoftwareUpdateHelper.getUpdateStatusIcon(item.statusId)}
            </div>
          </FlexibleRow.Column>
        </FlexibleRow.Stackable>
        <FlexibleRow.Stackable at='xl' width='65%'>
          <FlexibleRow.Stackable at='sm' width='34%'>
            <FlexibleRow.Column className='premier'>
              {item.softwareUpdateName}
            </FlexibleRow.Column>
          </FlexibleRow.Stackable>
          <FlexibleRow.Stackable at='sm' width='66%'>
            <FlexibleRow.Column width='50%'>
              {item.softwareName} {item.softwareVersionName}
            </FlexibleRow.Column>
            <FlexibleRow.Column width='50%' className='subtle'>
              {renderUpdateDateTime()}
            </FlexibleRow.Column>
          </FlexibleRow.Stackable>
        </FlexibleRow.Stackable>
        <FlexibleRow.Stackable at='lg' width='25%' minWidth='100px' className='text-right' wrap={true}>
          {renderPendingChildCount()}
          {renderProgressChildCount()}
          {renderSuccessfulChildCount()}
          {renderFailureChildCount()}
        </FlexibleRow.Stackable>
      </FlexibleRow>
    </a>
  );
}
